import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudUpload from '@mui/icons-material/CloudUpload';
import PropTypes from 'prop-types';

const UploadDropZoneDialogContainer = ({
  files,
  setFiles,
  errorMutation,
  setErrorMutation,
  filesType,
  accept,
  maxSize,
  multiple,
  onReject,
  sx,
  className,
  renderContent: RenderContent,
}) => {
  const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length) {
      if (onReject) {
        onReject(fileRejections);
      } else {
        setErrorMutation('Archivo no válido');
      }
      return;
    }
    setErrorMutation('');
    setFiles(acceptedFiles);
  };
  const {
    getRootProps, getInputProps, isDragActive,
  } = useDropzone({
    onDrop, accept, maxSize, multiple,
  });
  const boxTypography = () => {
    if (files.length === 1) {
      return (
        <Typography variant="h6" sx={{ margin: 2, wordBreak: 'break-word' }}>{files[0].name}</Typography>
      );
    }
    if (files.length > 1) {
      return (
        <Typography variant="h6" sx={{ borderRadius: 5, borderStyle: 'solid', borderWidth: '1px', padding: '10px' }}>
          {files.length} {filesType} Cargados
        </Typography>
      );
    }

    return (
      <><Typography variant="caption">Arrastra aquí el archivo que quieres subir</Typography>
        <Typography variant="caption" textAlign="center" mt={1}>ó</Typography>
      </>
    );
  };
  const ConditionalFilling = useMemo(() => {
    if (isDragActive) {
      return (
        <Grid item container direction="row" alignItems="center" justifyContent="center">
          <CloudUpload color="primary" sx={{ marginRight: 1 }} />
          <Typography>
            Suelta el archivo aquí!
          </Typography>
        </Grid>
      );
    }
    return (
      <Stack
        width="100%"
        height="100%"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        spacing={1}
      >
        <Typography
          variant="caption"
          width="90%"
          display="block"
          color="text.primary"
        >
          {files && errorMutation && errorMutation.split('\n').map((i, key) => <p key={`error-${key + 1}`}>{i}</p>)}
        </Typography>
        <Box minHeight="20%" sx={{ display: 'flex', flexDirection: 'column' }}>
          {boxTypography()}
        </Box>

        <Typography
          variant="caption"
          width="180px"
          color="primary"
          sx={{ textDecoration: 'underline' }}
        >Sube desde tu dispositivo
        </Typography>
      </Stack>
    );
  }, [isDragActive, errorMutation, files]);
  return (
    <Grid
      {...getRootProps()}
      onClick={(props) => { getRootProps().onClick(props); setErrorMutation(''); }}
      className={className}
      sx={[
        {
          borderColor: 'primary.main',
          backgroundColor: isDragActive ? 'primary.A100' : 'gray.A100',
          borderStyle: isDragActive ? 'dashed' : 'none',
          borderRadius: 5,
          height: '100%',
          width: '100%',
          cursor: 'pointer',
          transition: 'border 0.24s ease-in-out',
        },
        sx,
      ]}
      container
      justifyContent="center"
      alignItems="center"
    >
      <input {...getInputProps()} />
      {RenderContent
        ? (
          <RenderContent
            isDragActive={isDragActive}
            files={files}
            maxSize={maxSize}
            accept={accept}
          />
        )
        : ConditionalFilling}
    </Grid>
  );
};
UploadDropZoneDialogContainer.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  setFiles: PropTypes.func.isRequired,
  errorMutation: PropTypes.string,
  setErrorMutation: PropTypes.func,
  filesType: PropTypes.string,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  maxSize: PropTypes.number,
  multiple: PropTypes.bool,
  onReject: PropTypes.func,
  sx: PropTypes.shape({}),
  className: PropTypes.string,
  renderContent: PropTypes.elementType,
};
UploadDropZoneDialogContainer.defaultProps = {
  errorMutation: null,
  filesType: 'Archivos',
  setErrorMutation: () => {},
  accept: undefined,
  maxSize: undefined,
  multiple: true,
  onReject: null,
  sx: null,
  className: null,
  renderContent: null,
};
export default UploadDropZoneDialogContainer;
